<script>
import appConfig from "@/app.config"
import Layout from "@/views/layouts/main"
import PageHeader from "@/components/page-header"
import AtendimentoController from "@/controller/AtendimentoController"
import Swal from "sweetalert2"
import moment from "moment"

const STORAGE_KEY = 'sessoes-mensais-drafts'

function monthKeyFromDate(date) {
  return moment(date).format('YYYY-MM')
}

function monthLabelFromDate(date) {
  return moment(date).format('MMMM [de] YYYY')
}

function startOfMonth(date) {
  return moment(date).startOf('month')
}

function endOfMonth(date) {
  return moment(date).endOf('month')
}

function monthFromOffset(base, offset) {
  return moment(base).add(offset, 'month')
}

function cloneItem(item) {
  return JSON.parse(JSON.stringify(item))
}

export default {
  name: 'SessoesMensais',
  page: {
    title: 'Sessões mensais',
    meta: [
      {
        name: 'description',
        content: appConfig.description,
      },
    ],
  },
  components: { Layout, PageHeader },
  data() {
    const currentMonth = moment().startOf('month')
    return {
      controller: null,
      loading: false,
      saving: false,
      copyingPrevious: false,
      currentMonth,
      draftMonth: currentMonth.clone(),
      listAtendimentos: [],
      draftByMonth: {},
      activeDraftId: null,
      draftNote: '',
      breadcrumbItems: [
        { text: 'Dashboard', href: '/' },
        { text: 'Sessões mensais', active: true },
      ],
      monthOptions: Array.from({ length: 12 }, (_, index) => monthFromOffset(currentMonth, index - 6)),
    }
  },
  computed: {
    currentMonthKey() {
      return monthKeyFromDate(this.currentMonth)
    },
    draftMonthKey() {
      return monthKeyFromDate(this.draftMonth)
    },
    currentMonthLabel() {
      return monthLabelFromDate(this.currentMonth)
    },
    draftMonthLabel() {
      return monthLabelFromDate(this.draftMonth)
    },
    activeAttendances() {
      const active = (this.listAtendimentos || [])
        .filter((item) => this.isControlledAttendance(item) && this.isAttendanceActive(item))
        .map((item) => ({
          ...item,
          monthlyCount: this.getMonthlyCount(item),
          hasDraft: Boolean(this.getDraftForAttendance(item)),
        }))
      if (active.length > 0) return active
      return (this.listAtendimentos || [])
        .filter((item) => this.isAttendanceActive(item))
        .map((item) => ({
          ...item,
          monthlyCount: this.getMonthlyCount(item),
          hasDraft: Boolean(this.getDraftForAttendance(item)),
        }))
    },
    currentDrafts() {
      return this.getDraftsForMonth(this.draftMonthKey)
    },
    hasCurrentDrafts() {
      return this.currentDrafts.length > 0
    },
    unsavedDraftCount() {
      return this.getDraftsForMonth(this.draftMonthKey).length
    },
    monthRangeLabel() {
      return `${startOfMonth(this.currentMonth).format('DD/MM/YYYY')} até ${endOfMonth(this.currentMonth).format('DD/MM/YYYY')}`
    },
  },
  created() {
    this.controller = new AtendimentoController()
    this.currentUser = JSON.parse(localStorage.getItem('user'))
    this.currentEmpresa = JSON.parse(localStorage.getItem('currentEmpresa'))
    this.loadDraftsFromStorage()
  },
  mounted() {
    this.loadMonth(this.currentMonth)
  },
  methods: {
    monthKeyFromDate,
    monthLabelFromDate,
    startOfMonth,
    endOfMonth,
    isControlledAttendance(item) {
      return Boolean(item && item.control_sessions === true)
    },
    isAttendanceActive(item) {
      return !(item?.is_cancelled || item?.inactive || item?.status === 'inactive' || item?.status === 'cancelled')
    },
    getAttendanceLabel(item) {
      const patient = item?.pacient?.name || item?.patient_name || item?.name || 'Paciente não informado'
      const professional = item?.professional?.name || item?.professional_name || 'Profissional não informado'
      const treatment = item?.treatment?.name || item?.treatment_name || 'Tratamento não informado'
      const agreement = item?.agreement?.name || item?.agreement_name || 'Convênio não informado'
      return `${patient} • ${professional} • ${treatment} • ${agreement}`
    },
    getMonthlyCount(item) {
      const raw = item?.session_quantity ?? 0
      const parsed = Number(raw)
      return Number.isFinite(parsed) ? parsed : 0
    },
    getDraftStorage() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY)
        return raw ? JSON.parse(raw) : {}
      } catch (error) {
        return {}
      }
    },
    saveDraftStorage() {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.draftByMonth))
    },
    loadDraftsFromStorage() {
      this.draftByMonth = this.getDraftStorage()
      if (!this.draftByMonth || typeof this.draftByMonth !== 'object') {
        this.draftByMonth = {}
      }
    },
    getDraftsForMonth(monthKey) {
      return Array.isArray(this.draftByMonth[monthKey]) ? this.draftByMonth[monthKey] : []
    },
    getDraftForAttendance(item) {
      return this.getDraftsForMonth(this.draftMonthKey).find((draft) => draft.attendance_id === item.id)
        || this.getDraftsForMonth(this.currentMonthKey).find((draft) => draft.attendance_id === item.id)
        || null
    },

    buildDraftFromAttendance(item, overrides = {}) {
      const baseMonth = this.draftMonth.clone()
      const dateBase = item?.start_date ? moment(item.start_date) : baseMonth.clone().startOf('month')
      return {
        id: overrides.id || `${item.id}-${baseMonth.format('YYYYMM')}-${Date.now()}`,
        attendance_id: item.id,
        attendance_label: this.getAttendanceLabel(item),
        month: baseMonth.format('YYYY-MM'),
        start_date: overrides.start_date || dateBase.format('YYYY-MM-DD'),
        end_date: overrides.end_date || dateBase.endOf('month').format('YYYY-MM-DD'),
        notes: overrides.notes || '',
        status: overrides.status || 'draft',
        copied_from: overrides.copied_from || null,
        updated_at: new Date().toISOString(),
      }
    },
    async loadMonth(monthDate) {
      this.loading = true
      const month = moment(monthDate).startOf('month')
      this.currentMonth = month.clone()
      this.draftMonth = month.clone()
      try {
        const response = await this.controller.doGetTable({
          month: month.format('YYYY-MM'),
          date_ini: month.clone().startOf('month').format('YYYY-MM-DD'),
          date_end: month.clone().endOf('month').format('YYYY-MM-DD'),
        })
        const items = response?.data?.data || response?.data || response?.data?.items || response?.data?.atendimentos || []
        this.listAtendimentos = Array.isArray(items) ? items : []
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Erro ao carregar sessões',
          text: error?.response?.data?.message || 'Não foi possível carregar as sessões mensais.',
        })
        this.listAtendimentos = []
      } finally {
        this.loading = false
      }
    },
    changeMonth(offset) {
      const nextMonth = this.currentMonth.clone().add(offset, 'month')
      this.loadMonth(nextMonth)
    },
    goToToday() {
      this.loadMonth(moment())
    },
    selectDraftMonth(value) {
      this.draftMonth = moment(value).startOf('month')
    },
    createDraft(item) {
      const draft = this.buildDraftFromAttendance(item)
      const monthKey = this.draftMonthKey
      const drafts = [...this.getDraftsForMonth(monthKey)]
      const idx = drafts.findIndex((existing) => existing.attendance_id === item.id)
      if (idx >= 0) {
        drafts.splice(idx, 1, draft)
      } else {
        drafts.push(draft)
      }
      this.$set(this.draftByMonth, monthKey, drafts)
      this.saveDraftStorage()
      this.activeDraftId = draft.id
      this.draftNote = ''
      Swal.fire({
        icon: 'success',
        title: 'Rascunho criado',
        text: 'O item foi copiado para edição no mês selecionado.',
      })
    },
    copyPreviousMonth() {
      const previousMonthKey = this.currentMonth.clone().subtract(1, 'month').format('YYYY-MM')
      const previousDrafts = this.getDraftsForMonth(previousMonthKey)
      this.copyingPrevious = true
      try {
        if (!previousDrafts.length) {
          const baseDrafts = this.activeAttendances.map((item) => this.buildDraftFromAttendance(item, { copied_from: previousMonthKey }))
          this.$set(this.draftByMonth, this.currentMonthKey, baseDrafts)
        } else {
          const clones = previousDrafts.map((draft) => ({
            ...cloneItem(draft),
            id: `${draft.attendance_id}-${this.currentMonthKey}-${Date.now()}`,
            month: this.draftMonthKey,
            copied_from: previousMonthKey,
            updated_at: new Date().toISOString(),
          }))
          this.$set(this.draftByMonth, this.currentMonthKey, clones)
        }
        this.saveDraftStorage()
        Swal.fire({
          icon: 'success',
          title: 'Mês copiado',
          text: 'Os rascunhos do mês anterior foram copiados para o mês atual.',
        })
      } finally {
        this.copyingPrevious = false
      }
    },
    editDraft(draft) {
      this.activeDraftId = draft.id
      this.draftMonth = moment(`${draft.month}-01`)
      this.draftNote = draft.notes || ''
    },
    updateDraftField(draftId, field, value) {
      const monthKey = this.draftMonthKey
      const drafts = [...this.getDraftsForMonth(monthKey)]
      const index = drafts.findIndex((draft) => draft.id === draftId)
      if (index === -1) return
      drafts.splice(index, 1, {
        ...drafts[index],
        [field]: value,
        updated_at: new Date().toISOString(),
      })
      this.$set(this.draftByMonth, monthKey, drafts)
      this.saveDraftStorage()
    },
    removeDraft(draftId) {
      const monthKey = this.draftMonthKey
      const drafts = this.getDraftsForMonth(monthKey).filter((draft) => draft.id !== draftId)
      this.$set(this.draftByMonth, monthKey, drafts)
      if (this.activeDraftId === draftId) {
        this.activeDraftId = null
      }
      this.saveDraftStorage()
    },
    async confirmDrafts() {
      const drafts = this.getDraftsForMonth(this.draftMonthKey)
      if (!drafts.length) {
        Swal.fire({ icon: 'info', title: 'Nenhum rascunho', text: 'Crie ou copie um rascunho antes de confirmar.' })
        return
      }

      const { isConfirmed } = await Swal.fire({
        icon: 'question',
        title: 'Confirmar sessões mensais?',
        text: `Você está prestes a confirmar ${drafts.length} rascunho(s) do mês ${this.currentMonthLabel}.`,
        showCancelButton: true,
        confirmButtonText: 'Confirmar',
        cancelButtonText: 'Cancelar',
      })
      if (!isConfirmed) return

      this.saving = true
      try {
        const payload = {
          month: this.draftMonthKey,
          company_id: this.currentEmpresa?.id || null,
          drafts: drafts.map((draft) => ({
            attendance_id: draft.attendance_id,
            start_date: draft.start_date,
            end_date: draft.end_date,
            notes: draft.notes,
          })),
        }

        if (typeof this.controller.doPostLote === 'function') {
          await this.controller.doPostLote({
            empresa_id: payload.company_id,
            agendas: payload.drafts.map((draft) => ({
              data: `${draft.start_date} 00:00:00`,
              horario_inicio: '08:00',
              horario_fim: '08:30',
              profissional_id: draft.professional_id || this.listAtendimentos.find((item) => item.id === draft.attendance_id)?.professional_id || null,
              treatment_id: draft.treatment_id || this.listAtendimentos.find((item) => item.id === draft.attendance_id)?.treatment_id || null,
              patient_id: draft.patient_id || this.listAtendimentos.find((item) => item.id === draft.attendance_id)?.patient_id || null,
              atendimento_id: draft.attendance_id,
              observacao: draft.notes || `Sessões mensais ${payload.month}`,
            })),
            paciente_id: null,
            observacao: `Sessões mensais ${payload.month}`,
          })
        }

        this.$set(this.draftByMonth, this.currentMonthKey, [])
        this.saveDraftStorage()
        Swal.fire({ icon: 'success', title: 'Sessões confirmadas', text: 'Os rascunhos foram confirmados com sucesso.' })
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Falha ao confirmar',
          text: error?.response?.data?.message || error?.message || 'Não foi possível confirmar os rascunhos.',
        })
      } finally {
        this.saving = false
      }
    },
    draftSummary(draft) {
      return [draft.start_date, draft.end_date, draft.notes].filter(Boolean).join(' • ') || 'Sem detalhes'
    },
    hasEditableDraft(item) {
      return Boolean(this.getDraftForAttendance(item))
    },
  },
}
</script>

<template>
  <Layout>
    <PageHeader title="Sessões mensais" :items="breadcrumbItems" />

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
              <div>
                <h4 class="mb-1">Sessões mensais</h4>
                <p class="text-muted mb-0">
                  Controle o mês atual, reaproveite os dados do mês anterior e confirme os rascunhos ao final do fechamento.
                </p>
              </div>
              <div class="text-right">
                <div class="btn-group mb-2 mb-md-0" role="group" aria-label="Navegação de mês">
                  <button class="btn btn-outline-primary" type="button" @click="changeMonth(-1)">Mês anterior</button>
                  <button class="btn btn-outline-secondary" type="button" @click="goToToday">Mês atual</button>
                  <button class="btn btn-outline-primary" type="button" @click="changeMonth(1)">Próximo mês</button>
                </div>
                <div class="small text-muted">{{ currentMonthLabel }} • {{ monthRangeLabel }}</div>
              </div>
            </div>

            <div class="row mb-3">
              <div class="col-md-4 mb-2">
                <label class="form-label">Mês de edição</label>
                <input class="form-control" type="month" :value="draftMonthKey" @change="selectDraftMonth($event.target.value + '-01')" />
              </div>
              <div class="col-md-8 d-flex align-items-end justify-content-md-end gap-2 mb-2 flex-wrap">
                <button class="btn btn-outline-info" type="button" :disabled="copyingPrevious" @click="copyPreviousMonth">
                  <span v-if="copyingPrevious" class="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                  Copiar mês anterior
                </button>
                <button class="btn btn-primary" type="button" :disabled="saving" @click="confirmDrafts">
                  <span v-if="saving" class="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                  Confirmar rascunhos
                </button>
              </div>
            </div>

            <div v-if="loading" class="text-center py-5">
              <b-spinner class="align-middle mr-2"></b-spinner>
              <strong>Carregando sessões mensais...</strong>
            </div>

            <template v-else>
              <div class="row mb-3">
                <div class="col-md-6">
                  <div class="alert alert-info mb-0">
                    <strong>{{ activeAttendances.length }}</strong> atendimento(s) controlado(s) ativo(s) localizado(s) para o mês atual.
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="alert alert-secondary mb-0">
                    <strong>{{ unsavedDraftCount }}</strong> rascunho(s) salvo(s) para edição neste mês.
                  </div>
                </div>
              </div>

              <div class="table-responsive mb-4">
                <table class="table table-bordered table-striped align-middle">
                  <thead>
                    <tr>
                      <th>Atendimento controlado ativo</th>
                      <th class="text-center">Sessões/mês</th>
                      <th class="text-center">Rascunho</th>
                      <th class="text-center">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="activeAttendances.length === 0">
                      <td colspan="4" class="text-center text-muted">Nenhum atendimento controlado ativo encontrado.</td>
                    </tr>
                    <tr v-for="item in activeAttendances" :key="item.id">
                      <td>
                        <div class="font-weight-semibold">{{ getAttendanceLabel(item) }}</div>
                        <small class="text-muted">ID {{ item.id }} • {{ item.start_date || 'sem início' }} • {{ item.end_date || 'atual' }}</small>
                      </td>
                      <td class="text-center">{{ item.monthlyCount || '-' }}</td>
                      <td class="text-center">
                        <span v-if="item.hasDraft" class="badge badge-warning">Rascunho pronto</span>
                        <span v-else class="badge badge-light">Sem rascunho</span>
                      </td>
                      <td class="text-center">
                        <button class="btn btn-sm btn-outline-primary mr-2" type="button" @click="createDraft(item)">
                          Copiar para rascunho
                        </button>
                        <button class="btn btn-sm btn-outline-secondary" type="button" @click="editDraft(getDraftForAttendance(item))" :disabled="!hasEditableDraft(item)">
                          Editar rascunho
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="row">
                <div class="col-lg-7 mb-3">
                  <div class="border rounded p-3 h-100">
                    <h5 class="mb-3">Rascunhos do mês {{ draftMonthLabel }}</h5>
                    <div v-if="currentDrafts.length === 0" class="text-muted">
                      Nenhum rascunho encontrado para este mês.
                    </div>
                    <div v-for="draft in currentDrafts" :key="draft.id" class="border rounded p-3 mb-2" :class="{ 'border-primary': activeDraftId === draft.id }">
                      <div class="d-flex justify-content-between align-items-start flex-wrap">
                        <div>
                          <strong>{{ draft.attendance_label }}</strong>
                          <div class="small text-muted">{{ draftSummary(draft) }}</div>
                        </div>
                        <span class="badge badge-info text-uppercase">{{ draft.status }}</span>
                      </div>
                      <div class="row mt-3">
                        <div class="col-md-4 mb-2">
                          <label class="small mb-1">Início</label>
                          <input
                            class="form-control form-control-sm"
                            type="date"
                            :value="draft.start_date"
                            @input="updateDraftField(draft.id, 'start_date', $event.target.value)"
                          />
                        </div>
                        <div class="col-md-4 mb-2">
                          <label class="small mb-1">Fim</label>
                          <input
                            class="form-control form-control-sm"
                            type="date"
                            :value="draft.end_date"
                            @input="updateDraftField(draft.id, 'end_date', $event.target.value)"
                          />
                        </div>
                        <div class="col-md-4 mb-2">
                          <label class="small mb-1">Observação</label>
                          <input
                            class="form-control form-control-sm"
                            type="text"
                            :value="draft.notes"
                            placeholder="Observação do rascunho"
                            @input="updateDraftField(draft.id, 'notes', $event.target.value)"
                          />
                        </div>
                      </div>
                      <div class="d-flex justify-content-end">
                        <button class="btn btn-sm btn-outline-danger mr-2" type="button" @click="removeDraft(draft.id)">Excluir</button>
                        <button class="btn btn-sm btn-outline-primary" type="button" @click="editDraft(draft)">Selecionar</button>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-lg-5 mb-3">
                  <div class="border rounded p-3 h-100 bg-light">
                    <h5 class="mb-3">Fluxo de fechamento</h5>
                    <ul class="pl-3 mb-3 text-muted">
                      <li>Escolha o mês atual com os botões de navegação.</li>
                      <li>Copie os itens do mês anterior para acelerar o preenchimento.</li>
                      <li>Edite os rascunhos salvos antes da confirmação final.</li>
                      <li>Confirme para registrar o fechamento das sessões mensais.</li>
                    </ul>
                    <div class="alert alert-warning mb-0">
                      <strong>Atenção:</strong> a confirmação limpa os rascunhos do mês atual após o envio.
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
